import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";

import Button from "@/components/Button";
import { PageHeading, SectionHeading, SrOnly } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Th,
} from "@/components/Table";
import { api } from "@/utils/api";
import { IgProfile } from "@/types/resources";
import TableRow from "./components/TableRow";
import Form from "./components/Form";
import useSortState from "@/hooks/useSortState";
import SearchBar from "@/components/SearchBar";
import useDebounce from "@/hooks/useDebounce";

const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const NewProjectButton = styled(Button)`
  padding: 0 32px;
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    vertical-align: center;
    &:first-child {
      padding-left: 24px;
    }
  }
`;

const IgProfiles = () => {
  const [showModal, setShowModal] = useState(false);
  const { sort, setSort, sortQueryParamObj } = useSortState();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const {
    data: profiles,
    isError,
    isLoading,
  } = useQuery(
    ["ig_trackable_profiles", { sort, search: debouncedSearch }],
    () =>
      api.get<never, IgProfile[]>("/v1/ig-profiles-tracker/profiles", {
        params: {
          ...sortQueryParamObj,
          ...(!!debouncedSearch && {
            "filter[search]": debouncedSearch,
          }),
        },
      }),
    {
      staleTime: 120 * 1000,
    }
  );

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <HeadingsRow>
        <PageHeading>Instagram Profiles</PageHeading>
        <NewProjectButton onClick={() => setShowModal(true)}>
          Add New
        </NewProjectButton>
      </HeadingsRow>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by username"
      />
      <SectionHeading>Profiles List</SectionHeading>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                title="Username"
                property="username"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Tier"
                property="tier"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Posts Count"
                property="postsCount"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Created At"
                property="createdAt"
                sort={sort}
                setSort={setSort}
              />
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!profiles?.length}
              colSpan={5}
            >
              {profiles?.map((profile) => (
                <TableRow key={profile.id} profile={profile} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <AnimatePresence>
        {showModal && <Form onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </>
  );
};

export default IgProfiles;
